import { Tag } from 'antd';

/**
 * ActiveFiltersDisplay Molecule - Display active filters and sorting as removable tags
 * 
 * @param {Object} filters - Current filter values
 * @param {boolean} showPinnedOnly - Whether showing only pinned items
 * @param {Function} setFilters - Function to update filters
 * @param {Function} setShowPinnedOnly - Function to toggle show pinned only
 * @param {Object} sortConfig - Current sort configuration
 * @param {Function} clearSorting - Function to clear sorting
 * @param {number} totalCount - Total number of items (to show/hide component)
 */
const ActiveFiltersDisplay = ({
  filters,
  showPinnedOnly,
  setFilters,
  setShowPinnedOnly,
  sortConfig,
  clearSorting,
  totalCount,
}) => {
  // Don't render if no data
  if (totalCount === 0) {
    return null;
  }

  // Render active filter tags
  const renderActiveTags = () => {
    const activeTags = [];
    
    if (filters.businessArea) {
      activeTags.push({ key: 'businessArea', value: filters.businessArea });
    }
    
    if (filters.updatedBy) {
      activeTags.push({ key: 'updatedBy', value: filters.updatedBy });
    }
    
    if (filters.lastUpdated) {
      const lastUpdatedText = {
        'Today': 'Today',
        'This-Week': 'This Week',
        'This-Month': 'This Month'
      }[filters.lastUpdated];
      activeTags.push({ key: 'lastUpdated', value: `Last Updated: ${lastUpdatedText}` });
    }
    
    if (showPinnedOnly) {
      activeTags.push({ key: 'pinnedOnly', value: 'Favorites' });
    }

    return activeTags.map(tag => (
      <Tag
        key={tag.key}
        closable
        onClose={() => {
          if (tag.key === 'pinnedOnly') {
            setShowPinnedOnly(false);
          } else {
            setFilters(prev => ({ ...prev, [tag.key]: '' }));
          }
        }}
        className="bg-teal-100 text-teal-700 border-teal-500 text-sm border-[1.5px] px-3 py-1 mr-2 font-bold rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 ease-in-out"
      >
        {tag.value}
      </Tag>
    ));
  };

  // Render active sorting tag
  const renderActiveSortingTag = () => {
    if (!sortConfig.key) {
      return null;
    }

    return (
      <Tag
        closable
        onClose={clearSorting}
        className="bg-amber-100 text-amber-700 border-amber-500 border-[1.5px] px-3 py-1 mr-2 font-bold rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 ease-in-out"
      >
        {`Sorted by ${sortConfig.key.replace(/([A-Z])/g, ' $1')} (${sortConfig.direction})`}
      </Tag>
    );
  };

  const activeTags = renderActiveTags();
  const sortingTag = renderActiveSortingTag();

  // Don't render if no active filters or sorting
  if (activeTags.length === 0 && !sortingTag) {
    return null;
  }

  return (
    <div className="mb-2">
      {activeTags}
      {sortingTag}
    </div>
  );
};

export default ActiveFiltersDisplay;
